import type { Chat, ClientConfig, UploadFile } from '@liniya/api-client';
import { referenceExcerpt } from '@liniya/api-client';
import { Forward, Paperclip, Reply, Send, SmilePlus, X } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type KeyboardEvent } from 'react';

import { EmojiPicker } from './EmojiPicker';
import { clipboardMedia } from './media-transfer';
import { useAttachFile } from './useAttachFile';
import { VoiceRecorder } from './VoiceRecorder';
import { useComposerStore } from '@liniya/client-core';
import { useRealtime } from '@/websocket/provider';

interface Props {
  chat: Chat;
  disabled: boolean;
  limits: ClientConfig['limits'] | undefined;
  onSend: (input: {
    text: string;
    attachment?: { file: UploadFile; fileName: string; previewUrl: string };
  }) => void;
  onSubmitEdit: (messageId: string, content: string) => void;
}

const TYPING_SIGNAL_INTERVAL_MS = 3000;

/**
 * Whether a paste that reached the document was meant for the composer.
 *
 * Anything typed into is somebody else's: a paste into the user search or a
 * renaming field belongs to that field even when the clipboard holds a picture.
 * Everything else — the message box itself, a bubble, the page with nothing
 * focused at all — is the conversation.
 */
function pasteBelongsToComposer(target: EventTarget | null, own: HTMLTextAreaElement | null): boolean {
  if (target === own) return true;
  if (!(target instanceof HTMLElement)) return true;
  if (target.isContentEditable) return false;
  return target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA';
}

export function MessageComposer({ chat, disabled, limits, onSend, onSubmitEdit }: Props) {
  const textarea = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const lastTypingSignal = useRef(0);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const { client } = useRealtime();

  const draft = useComposerStore((state) => state.drafts[chat.id] ?? '');
  const reference = useComposerStore((state) => state.references[chat.id] ?? null);
  const attachment = useComposerStore((state) => state.attachments[chat.id] ?? null);
  const editing = useComposerStore((state) => state.editing[chat.id] ?? null);
  const setDraft = useComposerStore((state) => state.setDraft);
  const setReference = useComposerStore((state) => state.setReference);
  const setAttachment = useComposerStore((state) => state.setAttachment);
  const stopEditing = useComposerStore((state) => state.stopEditing);
  const clearComposer = useComposerStore((state) => state.clearComposer);
  const attach = useAttachFile(chat.id, limits);

  // A textarea that grows with its content, capped so it cannot swallow the
  // conversation on a phone.
  useEffect(() => {
    const node = textarea.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, 140)}px`;
  }, [draft]);

  const ready = Boolean(draft.trim() || attachment || reference?.kind === 'forward');

  /**
   * Tell the other side that typing is happening, at most once every few
   * seconds. Signalling on every keystroke would spend the connection's event
   * budget on an indicator, and the server-side expiry means a slightly stale
   * signal costs nothing.
   */
  const signalTyping = () => {
    if (!client?.isReady()) return;
    const now = Date.now();
    if (now - lastTypingSignal.current < TYPING_SIGNAL_INTERVAL_MS) return;
    lastTypingSignal.current = now;
    client.trySend('typing.start', { chat_id: chat.id });
  };

  const stopTyping = () => {
    lastTypingSignal.current = 0;
    client?.trySend('typing.stop', { chat_id: chat.id });
  };

  const pickFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    attach([{ file, name: file.name }]);
  };

  /**
   * Ctrl+V as a way to attach a screenshot or a copied file.
   *
   * Listening on the document rather than on the textarea is deliberate:
   * pasting is something people do to the conversation, not to a particular
   * box, and requiring a click in the field first would make the shortcut work
   * only when it was least needed. Pastes aimed at another field — the search
   * box, a dialog's input — are left alone, so nothing is stolen from them.
   */
  useEffect(() => {
    // Editing changes text that is already sent, and a message cannot grow an
    // attachment after the fact — the same reason the paperclip is disabled.
    if (disabled || editing) return;

    const onPaste = (event: ClipboardEvent) => {
      if (!pasteBelongsToComposer(event.target, textarea.current)) return;
      const media = clipboardMedia(event.clipboardData);
      if (media.length === 0) return;

      // A file is nothing the browser could have inserted as text, so taking
      // the paste over loses nothing.
      event.preventDefault();
      attach(media);
      // The caption is the next thing anybody types.
      textarea.current?.focus();
    };

    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('paste', onPaste);
    };
  }, [attach, disabled, editing]);

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (disabled) return;

    if (editing) {
      const content = draft.trim();
      if (!content) return;
      onSubmitEdit(editing.messageId, content);
      stopEditing(chat.id);
      return;
    }

    if (!ready) return;
    onSend({
      text: draft,
      ...(attachment
        ? {
            attachment: {
              file: attachment.file,
              fileName: attachment.name,
              previewUrl: attachment.previewUrl,
            },
          }
        : {}),
    });
    // The attachment now belongs to the message in the history: clearing the
    // composer here means a transport error cannot make it reappear and be
    // sent twice.
    clearComposer(chat.id);
    stopTyping();
    textarea.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
      return;
    }
    if (event.key === 'Escape' && editing) {
      stopEditing(chat.id);
    }
  };

  if (chat.kind === 'channel' && !chat.can_publish) {
    return (
      <div className="channel-subscriber-notice">
        Публиковать сообщения могут только владелец и администраторы канала.
      </div>
    );
  }

  return (
    <form className="composer" onSubmit={submit}>
      {editing ? (
        <div className="composer-reference">
          <Reply size={16} aria-hidden="true" />
          <div>
            <strong>Редактирование сообщения</strong>
            <span>{referenceExcerpt(editing.original)}</span>
          </div>
          <button
            className="icon-btn"
            type="button"
            aria-label="Отменить редактирование"
            onClick={() => stopEditing(chat.id)}
          >
            <X size={16} />
          </button>
        </div>
      ) : null}

      {reference ? (
        <div className="composer-reference">
          {reference.kind === 'reply' ? <Reply size={16} /> : <Forward size={16} />}
          <div>
            <strong>
              {reference.kind === 'reply' ? 'Ответ на сообщение' : 'Пересылка сообщения'}
            </strong>
            <span>{reference.excerpt}</span>
          </div>
          <button
            className="icon-btn"
            type="button"
            aria-label="Отменить действие"
            onClick={() => setReference(chat.id, null)}
          >
            <X size={16} />
          </button>
        </div>
      ) : null}

      {attachment ? (
        <div className="composer-attachment">
          <span className="composer-attachment-preview">
            {attachment.kind === 'image' ? (
              <img src={attachment.previewUrl} alt="Выбранное изображение" />
            ) : attachment.kind === 'video' ? (
              <video src={attachment.previewUrl} muted playsInline />
            ) : (
              <Paperclip size={20} />
            )}
          </span>
          <span className="chat-copy" style={{ minWidth: 0 }}>
            <strong style={{ fontSize: 13 }}>{attachment.name}</strong>
            <span className="chat-preview">
              {(attachment.size / 1024 / 1024).toFixed(1)} МБ
            </span>
          </span>
          <button
            className="icon-btn"
            type="button"
            aria-label="Убрать вложение"
            onClick={() => setAttachment(chat.id, null)}
          >
            <X size={16} />
          </button>
        </div>
      ) : null}

      <input
        ref={fileInput}
        className="sr-only"
        type="file"
        onChange={pickFile}
        aria-hidden="true"
        tabIndex={-1}
      />

      <button
        className="icon-btn"
        type="button"
        aria-label="Прикрепить файл"
        title="Фото, видео или файл. Можно вставить из буфера (Ctrl+V) или перетащить в чат"
        disabled={disabled || Boolean(editing)}
        onClick={() => fileInput.current?.click()}
      >
        <Paperclip size={20} />
      </button>

      <textarea
        ref={textarea}
        rows={1}
        value={draft}
        disabled={disabled}
        placeholder={
          disabled
            ? 'Отправка недоступна'
            : chat.kind === 'channel'
              ? 'Публикация'
              : 'Сообщение'
        }
        aria-label={chat.kind === 'channel' ? 'Публикация канала' : 'Сообщение'}
        onChange={(event) => {
          setDraft(chat.id, event.target.value);
          signalTyping();
        }}
        onBlur={stopTyping}
        onKeyDown={onKeyDown}
      />

      <div style={{ position: 'relative' }}>
        <button
          className="icon-btn"
          type="button"
          aria-label="Эмодзи"
          aria-expanded={emojiOpen}
          disabled={disabled}
          onClick={() => setEmojiOpen((open) => !open)}
        >
          <SmilePlus size={20} />
        </button>
        {emojiOpen ? (
          <EmojiPicker
            onPick={(emoji) => {
              setDraft(chat.id, draft + emoji);
              setEmojiOpen(false);
              textarea.current?.focus();
            }}
            onClose={() => setEmojiOpen(false)}
          />
        ) : null}
      </div>

      {/*
        Send or record, never both: with nothing written there is nothing to
        send, and an inert button in that spot is a control that only ever
        refuses.
      */}
      {ready || editing ? (
        <button
          className="send ready"
          type="submit"
          disabled={disabled}
          aria-label={editing ? 'Сохранить' : chat.kind === 'channel' ? 'Опубликовать' : 'Отправить'}
        >
          <Send size={19} />
        </button>
      ) : (
        <VoiceRecorder
          disabled={disabled}
          onRecorded={(file) => {
            // Sent straight away: stopping the recording was the send.
            onSend({ text: '', attachment: { file, fileName: file.name, previewUrl: '' } });
          }}
        />
      )}
    </form>
  );
}
